<script setup lang="ts">
/**
 * A busy indicator (issue 2085): a turning circle with words beside it, so the state is read, never carried by motion
 * alone. It is a `role="status"` region, so a screen reader announces the label when it appears. The circle stops
 * turning when the person asks the system for reduced motion (`motion-reduce:`). Colour comes from theme.css only.
 */

import { LoaderCircleIcon } from '@lucide/vue'
import type { HTMLAttributes } from 'vue'
import { cn } from '../../../lib/utils'

interface SpinnerProps {
    /** The words read beside the circle, such as `Uploading…`. */
    label?: string
    class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<SpinnerProps>(), { label: 'Loading…', class: undefined })
</script>

<template>
  <span
    data-slot="spinner"
    role="status"
    aria-live="polite"
    :class="cn('inline-flex items-center gap-1.5 text-muted-foreground', props.class)"
  >
    <LoaderCircleIcon aria-hidden="true" class="size-3.5 shrink-0 animate-spin motion-reduce:animate-none" />
    <span>{{ props.label }}</span>
  </span>
</template>
